import React, { useEffect, useMemo, useState } from "react";
import { Plus, Sparkles } from "lucide-react";
import { api, imageUrl } from "../lib/api";
import { useI18n } from "../i18n";

const PLACEHOLDER = "https://customer-assets.emergentagent.com/job_store-hub-157/artifacts/yyc18991_IMG_4514.jpeg";

function pickSuggestions(products, items, left) {
  const inCart = new Set(items.map((i) => i.product_id));
  const pool = products
    .filter((p) => !inCart.has(p.id) && p.quantity > 0 && p.in_stock !== false)
    .map((p) => {
      const tier = Array.isArray(p.tiers) && p.tiers.length ? p.tiers.filter((x) => x?.label)[0] : null;
      return { ...p, tier, price: tier ? tier.price : p.price, label: tier ? `${p.name} · ${tier.label}` : p.name };
    });
  const unlock = pool.filter((p) => p.price >= left).sort((a, b) => a.price - b.price);
  const rest = pool.filter((p) => p.price < left).sort((a, b) => (b.best_seller ? 1 : 0) - (a.best_seller ? 1 : 0) || b.price - a.price);
  return [...unlock.slice(0, 1), ...rest, ...unlock.slice(1)].slice(0, 3);
}

export function CartCrossSell({ vendorId, items, left, onAdd }) {
  const { t } = useI18n();
  const [products, setProducts] = useState([]);

  useEffect(() => {
    if (!vendorId) return;
    api.get(`/vendors/${vendorId}/products`).then((r) => setProducts(r.data || [])).catch(() => setProducts([]));
  }, [vendorId]);

  const picks = useMemo(() => pickSuggestions(products, items, left), [products, items, left]);
  if (left <= 0 || picks.length === 0) return null;

  return (
    <div data-testid="cart-cross-sell" className="border border-zinc-800 border-t-0 bg-ink-surface p-3">
      <p className="text-[11px] uppercase font-bold text-zinc-400 flex items-center gap-1.5 mb-2">
        <Sparkles className="h-3.5 w-3.5 text-neon-magenta" /> {t("co.xsTitle")}
      </p>
      <div className="grid grid-cols-3 gap-2">
        {picks.map((p) => {
          const unlocks = p.price >= left;
          return (
            <div key={p.id} data-testid="cross-sell-item" className={`relative flex flex-col border p-2 ${unlocks ? "border-neon-green/60" : "border-zinc-800"}`}>
              {unlocks && <span data-testid="cross-sell-unlock" className="absolute -top-2 left-1 bg-neon-green text-black text-[9px] uppercase font-bold px-1.5 py-0.5">{t("co.xsUnlock")}</span>}
              <img src={imageUrl(p.image_url) || PLACEHOLDER} alt={p.name} loading="lazy" className="h-16 w-full object-cover mb-1.5" />
              <p className="text-white text-[11px] font-semibold leading-tight line-clamp-2 flex-1">{p.label}</p>
              <div className="flex items-center justify-between mt-1.5 gap-1">
                <span className="text-neon-green font-anton text-sm">{p.price.toFixed(2)}€</span>
                <button type="button" data-testid="cross-sell-add" onClick={() => onAdd(p.tier ? { ...p, selectedTier: p.tier } : p)} aria-label={t("co.xsAdd")}
                  className="h-7 w-7 flex items-center justify-center bg-neon-green text-black hover:bg-neon-greenDark transition-colors">
                  <Plus className="h-4 w-4" />
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
